import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  useProjectEdition,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { ResourcesIcon } from "./resourcesIcon";
import { ExpenseIcon } from "./expenseIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function toFixed(num: number | string) {
  const nbr = typeof num === "string" ? parseFloat(num) : num;

  return typeof num !== "undefined" && num !== ""
    ? Math.round(nbr) === nbr
      ? nbr.toString()
      : nbr.toFixed(2)
    : "";
}

function BudgetSection({ project }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const [resourcesValue, setResourcesValue] = React.useState(
    project.allocatedResourcesBudget,
  );
  const [expensesValue, setExpensesValue] = React.useState(
    project.allocatedExpensesBudget,
  );

  const { isRequestPending, editProject } = useProjectEdition(project);

  React.useEffect(() => {
    setResourcesValue(
      parseFloat(String(project.allocatedResourcesBudget || 0)),
    );
  }, [project.allocatedResourcesBudget]);

  React.useEffect(() => {
    setExpensesValue(parseFloat(String(project.allocatedExpensesBudget || 0)));
  }, [project.allocatedExpensesBudget]);

  const totalAllocatedBudget =
    (project.allocatedResourcesBudget || 0) +
    (project.allocatedExpensesBudget || 0);

  const handleResourcesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editProject({
      project,
      nextProject: {
        ...project,
        allocatedResourcesBudget: resourcesValue,
      },
    });
  };

  const handleExpensesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editProject({
      project,
      nextProject: {
        ...project,
        allocatedExpensesBudget: expensesValue,
      },
    });
  };

  const formatBudget = (value: number) => {
    return Number(value) >= 1000
      ? `${toFixed(Number(value) / 1000)}k`
      : toFixed(value);
  };

  return (
    <div className={styles.budgetContainer}>
      <div className={styles.budgetGroup}>
        <h4 className={styles.groupTitle}>
          {t("projectDetails.budget.allocated")}
        </h4>
        <div className={styles.groupRows}>
          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              <ResourcesIcon className={styles.icon} />
              <span>{t("projectDetails.budget.allocatedResourcesBudget")}</span>
            </div>
            <form className={styles.group} onSubmit={handleResourcesSubmit}>
              <input
                type="number"
                value={resourcesValue}
                className={styles.input}
                step="0.5"
                onChange={({ target }) =>
                  setResourcesValue(
                    !!target.value ? parseFloat(target.value) : 0,
                  )
                }
                onBlur={() =>
                  !isRequestPending &&
                  resourcesValue !== project.allocatedResourcesBudget &&
                  editProject({
                    project,
                    nextProject: {
                      ...project,
                      allocatedResourcesBudget: resourcesValue,
                    },
                  })
                }
                disabled={isRequestPending}
              />
              <p className={styles.value}>€</p>
            </form>
          </div>

          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              <ExpenseIcon className={styles.icon} />
              <span>{t("projectDetails.budget.allocatedExpensesBudget")}</span>
            </div>
            <form className={styles.group} onSubmit={handleExpensesSubmit}>
              <input
                type="number"
                value={expensesValue}
                className={styles.input}
                step="0.5"
                onChange={({ target }) =>
                  setExpensesValue(
                    !!target.value ? parseFloat(target.value) : 0,
                  )
                }
                onBlur={() =>
                  !isRequestPending &&
                  expensesValue !== project.allocatedExpensesBudget &&
                  editProject({
                    project,
                    nextProject: {
                      ...project,
                      allocatedExpensesBudget: expensesValue,
                    },
                  })
                }
                disabled={isRequestPending}
              />
              <p className={styles.value}>€</p>
            </form>
          </div>

          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              <span className={styles.totalLabel}>
                {t("projectDetails.budget.allocatedBudget")}
              </span>
            </div>
            <div className={styles.group}>
              <div className={classNames(styles.totalValue, styles.value)}>
                {formatBudget(totalAllocatedBudget)}
              </div>
              <p className={styles.value}>€</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.budgetGroup}>
        <h4 className={styles.groupTitle}>
          {t("projectDetails.budget.costs")}
        </h4>
        <div className={styles.groupRows}>
          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              {t("projectSection.columnNames.budget_plannedBudget")}
            </div>
            <div className={styles.group}>
              <div className={styles.value}>
                {formatBudget(project.finalPlannedValueOfProject)}
              </div>
              <p className={styles.value}>€</p>
            </div>
          </div>
          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              {t("projectSection.columnNames.budget_realCosts")}
            </div>
            <div className={styles.group}>
              <div
                className={classNames(styles.value, {
                  [styles.warning]:
                    project.trackingRealCost >
                    project.finalPlannedValueOfProject,
                })}
              >
                {formatBudget(project.trackingRealCost)}
              </div>
              <p className={styles.value}>€</p>
            </div>
          </div>

          {planzone.uuid !== "57ec7e80-dcbb-49af-b11f-665fa6e545c1" && (
            <div className={styles.budgetRow}>
              <div className={styles.budgetLabel}>
                {t("projectSection.columnNames.budget_acquiredValue")}
              </div>
              <div className={styles.group}>
                <div className={styles.value}>
                  {formatBudget(project.trackingAcquiredValue)}
                </div>
                <p className={styles.value}>€</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.budgetGroup}>
        <h4 className={styles.groupTitle}>
          {t("projectDetails.budget.margins")}
        </h4>
        <div className={styles.groupRows}>
          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              {t("projectSection.columnNames.budget_plannedMargin")}
            </div>
            <div className={styles.group}>
              <div
                className={classNames(styles.value, {
                  [styles.warning]: project.plannedMarginBudget < 0,
                  [styles.positive]: project.plannedMarginBudget > 0,
                })}
              >
                {formatBudget(project.plannedMarginBudget)}
              </div>
              <p className={styles.value}>€</p>
            </div>
          </div>

          <div className={styles.budgetRow}>
            <div className={styles.budgetLabel}>
              {t("projectSection.columnNames.budget_realMargin")}
            </div>
            <div className={styles.group}>
              <div
                className={classNames(styles.value, {
                  [styles.warning]: project.realMarginBudget < 0,
                  [styles.positive]: project.realMarginBudget > 0,
                })}
              >
                {formatBudget(project.realMarginBudget)}
              </div>
              <p className={styles.value}>€</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { BudgetSection };
